// @ts-check
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Grid, IconButton, Tooltip } from '@mui/material';
import { Topheader } from '../../components/Topheader';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import TableV2, { spreadPaginationOptions } from '../../components/Table/TableV2';
import { ArrowOutward } from '@mui/icons-material';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
import locationFeatureFlagService from '../../services/locationFeatureFlagService';
import LocationFeatureFlagConfirmDialog from './LocationFeatureFlagConfirmDialog';
import LocationFeatureFlagSelect from './LocationFeatureFlagSelect';

/** @typedef {import('../../../../types/common').ObjectIdType} ObjectIdType */
/** @typedef {import('../../../../types/common').PaginationOptions} PaginationOptions */
/** @typedef {import('../../../../types/locationFeatureFlags').LocationFeatureFlagListViewModel} LocationFeatureFlagRow */
/** @typedef {Omit<LocationFeatureFlagRow, 'id'> & {id: string}} LocationFeatureFlagTableRow */
/** @typedef {Omit<PaginationOptions, 'fromDate' | 'toDate'> & {fromDate?: string, toDate?: string}} LocationFeatureFlagQuery */
/** @typedef {import('../../../../types/featureFlag').FeatureFlag} FeatureFlag */
/** @typedef {{scope: 'company', companyId: ObjectIdType, companyName?: string, featureFlag: FeatureFlag, description: string, action: 'allow' | 'deny'} | {scope: 'featureFlag', featureFlag: FeatureFlag, description: string, action: 'allow' | 'deny'} | null} BulkFeatureFlagSelection */
/**
 * @typedef {Object} AlertMessage
 * @property {string} message
 * @property {string} severity
 */

/**
 * @param {LocationFeatureFlagRow} row
 * @returns {string}
 */
const getRowId = (row) => String(row.id || `${row.locationId}-${row.featureFlag}`);

const LocationFeatureFlags = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const [messages, setMessages] = useState(/** @type {AlertMessage[]} */ ([]));
  const [rowCount, setRowCount] = useState(0);
  const [locationFeatureFlags, setLocationFeatureFlags] = useState(/** @type {LocationFeatureFlagTableRow[]} */ ([]));
  const [savingFeatureFlagId, setSavingFeatureFlagId] = useState(/** @type {string | null} */ (null));
  const [bulkFeatureFlagSelection, setBulkFeatureFlagSelection] = useState(
    /** @type {BulkFeatureFlagSelection} */ (null)
  );
  const [bulkAction, setBulkAction] = useState(/** @type {'allow' | 'deny'} */ ('allow'));
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  useEffect(() => {
    if (isForbidden) {
      history.push('/Dashboard');
    }
  }, [history, isForbidden]);

  /**
   * @param {PaginationOptions} paginationOptions
   */
  const fetchLocationFeatureFlags = (paginationOptions) => {
    const query = spreadPaginationOptions(paginationOptions);
    /** @type {LocationFeatureFlagQuery} */
    const requestQuery = {
      ...query,
      page: paginationOptions.page,
      pageSize: paginationOptions.pageSize,
    };

    setIsLoading(true);
    return locationFeatureFlagService
      .getLocationFeatureFlags(requestQuery)
      .then((response) => {
        if (response.data) {
          /** @type {LocationFeatureFlagRow[]} */
          const rows = response.data.locationFeatureFlags ?? [];
          setLocationFeatureFlags(rows.map((row) => ({ ...row, id: getRowId(row) })));
          setRowCount(response.data.rowCount ?? 0);
        }
      })
      .catch((error) => {
        if (error?.response?.status === 403) {
          setIsForbidden(true);
          setMessages([]);
          return;
        }

        setMessages([
          {
            message: `Could not retrieve location feature flags. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * @param {LocationFeatureFlagTableRow} row
   * @param {'allow' | 'deny'} action
   * @returns {Promise<void>}
   */
  const handleFeatureFlagActionChange = (row, action) => {
    setSavingFeatureFlagId(row.id);
    return locationFeatureFlagService
      .saveLocationFeatureFlag({
        locationId: row.locationId,
        featureFlag: row.featureFlag,
        action,
      })
      .then(() => {
        setLocationFeatureFlags((currentRows) =>
          currentRows.map((currentRow) => (currentRow.id === row.id ? { ...currentRow, action } : currentRow))
        );
        setMessages([]);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not update location feature flag. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setSavingFeatureFlagId(null);
      });
  };

  /**
   * @param {LocationFeatureFlagTableRow} row
   * @param {'company' | 'featureFlag'} scope
   * @param {React.MouseEvent} event
   */
  const handleOpenBulkDialog = (row, scope, event) => {
    event.stopPropagation();
    if (scope === 'company') {
      setBulkFeatureFlagSelection({
        scope: 'company',
        companyId: row.companyId,
        companyName: row.companyName,
        featureFlag: row.featureFlag,
        description: row.description,
        action: row.action,
      });
    } else if (scope === 'featureFlag') {
      setBulkFeatureFlagSelection({
        scope: 'featureFlag',
        featureFlag: row.featureFlag,
        description: row.description,
        action: row.action,
      });
    } else {
      return;
    }
    setBulkAction(row.action);
  };

  const handleCloseBulkDialog = () => {
    if (!isBulkSaving) {
      setBulkFeatureFlagSelection(null);
    }
  };

  /** @returns {Promise<void>} */
  const handleBulkFeatureFlagActionChangeByCompany = () => {
    if (bulkFeatureFlagSelection?.scope !== 'company') {
      return Promise.resolve();
    }

    const selection = bulkFeatureFlagSelection;
    setIsBulkSaving(true);
    return locationFeatureFlagService
      .bulkSaveLocationFeatureFlagByCompanyId({
        companyId: selection.companyId,
        featureFlag: selection.featureFlag,
        action: bulkAction,
      })
      .then(() => {
        setLocationFeatureFlags((currentRows) =>
          currentRows.map((currentRow) => {
            if (currentRow.companyId === selection.companyId && currentRow.featureFlag === selection.featureFlag) {
              return { ...currentRow, action: bulkAction };
            }

            return currentRow;
          })
        );
        setMessages([]);
        setBulkFeatureFlagSelection(null);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not update company location feature flag. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsBulkSaving(false);
      });
  };

  /** @returns {Promise<void>} */
  const handleBulkFeatureFlagActionChangeByFeatureFlag = () => {
    if (bulkFeatureFlagSelection?.scope !== 'featureFlag') {
      return Promise.resolve();
    }

    const selection = bulkFeatureFlagSelection;
    setIsBulkSaving(true);
    return locationFeatureFlagService
      .bulkSaveLocationFeatureFlagByFeatureFlag({
        featureFlag: selection.featureFlag,
        action: bulkAction,
      })
      .then(() => {
        setLocationFeatureFlags((currentRows) =>
          currentRows.map((currentRow) => {
            if (currentRow.featureFlag === selection.featureFlag) {
              return { ...currentRow, action: bulkAction };
            }

            return currentRow;
          })
        );
        setMessages([]);
        setBulkFeatureFlagSelection(null);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not update featureFlag location feature flag. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsBulkSaving(false);
      });
  };

  /** @returns {Promise<void>} */
  const handleBulkFeatureFlagSubmit = () => {
    if (!bulkFeatureFlagSelection) {
      return Promise.resolve();
    }

    if (bulkFeatureFlagSelection.scope === 'company') {
      return handleBulkFeatureFlagActionChangeByCompany();
    }

    if (bulkFeatureFlagSelection.scope === 'featureFlag') {
      return handleBulkFeatureFlagActionChangeByFeatureFlag();
    }

    return Promise.resolve();
  };

  /** @param {{ row: LocationFeatureFlagTableRow }} cellParams */
  const renderFeatureFlagCell = (cellParams) => {
    return (
      <Tooltip title={cellParams.row.description} placement="top-start" arrow>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span>{cellParams.row.featureFlag}</span>
          <IconButton
            aria-label={`Change feature flag action for all locations for feature flag ${cellParams.row.featureFlag}`}
            title={`Change feature flag action for all locations for feature flag ${cellParams.row.featureFlag}`}
            onClick={(event) => handleOpenBulkDialog(cellParams.row, 'featureFlag', event)}
            size="small"
          >
            <ArrowOutward fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  /** @param {{ row: LocationFeatureFlagTableRow }} cellParams */
  const renderCompanyCell = (cellParams) => {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <span>{cellParams.row.companyName}</span>
        <IconButton
          aria-label={`Change feature flag action for all locations in company ${cellParams.row.companyName}`}
          title={`Change feature flag action for all locations in company ${cellParams.row.companyName}`}
          onClick={(event) => handleOpenBulkDialog(cellParams.row, 'company', event)}
          size="small"
        >
          <ArrowOutward fontSize="small" />
        </IconButton>
      </span>
    );
  };

  /** @param {{ row: LocationFeatureFlagTableRow }} cellParams */
  const renderActionCell = (cellParams) => {
    return (
      <LocationFeatureFlagSelect
        value={cellParams.row.action}
        disabled={savingFeatureFlagId === cellParams.row.id}
        featureFlag={cellParams.row.featureFlag}
        onChange={(action) => handleFeatureFlagActionChange(cellParams.row, action)}
      />
    );
  };

  const columns = [
    {
      field: 'companyName',
      type: 'string',
      headerName: 'Company',
      minWidth: 200,
      flex: 1,
      renderCell: renderCompanyCell,
    },
    { field: 'locationName', type: 'string', headerName: 'Location', minWidth: 200, flex: 1 },
    {
      field: 'featureFlag',
      type: 'string',
      headerName: 'Feature Flag',
      minWidth: 200,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span>Feature Flag</span>
          <span style={{ display: 'inline-flex', verticalAlign: 'middle', position: 'relative', top: '2px' }}>
            <InfoTooltip message="Hover over the feature flag to see description." />
          </span>
        </span>
      ),
      renderCell: renderFeatureFlagCell,
    },
    {
      field: 'action',
      type: 'string',
      headerName: 'Action',
      minWidth: 130,
      flex: 1,
      renderCell: renderActionCell,
    },
    { field: 'gateway', type: 'string', headerName: 'Gateway', minWidth: 130, flex: 1 },
  ];

  return (
    <div className="rightPanel">
      <Topheader />
      <div className="contentPart">
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginBottom: '40px' }}
          className="terminal-container term-add"
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <h3 className="terminal-title">Feature Flags</h3>
            </Grid>
            {isForbidden ? (
              <Grid item xs={12}>
                <h4>Forbidden</h4>
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <FlexAlerts messages={messages} />
                </Grid>
                <Grid item xs={12}>
                  <TableV2
                    rows={locationFeatureFlags}
                    columns={columns}
                    fetchData={fetchLocationFeatureFlags}
                    rowCount={rowCount}
                    isLoading={isLoading}
                    isExportable
                    searchConfig={{ isSearchBar: true }}
                  />
                </Grid>
                <LocationFeatureFlagConfirmDialog
                  selection={bulkFeatureFlagSelection}
                  action={bulkAction}
                  isSaving={isBulkSaving}
                  onActionChange={setBulkAction}
                  onClose={handleCloseBulkDialog}
                  onSubmit={handleBulkFeatureFlagSubmit}
                />
              </>
            )}
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default LocationFeatureFlags;
